import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { bookingsApi, installmentsApi, documentsApi } from '../api/endpoints';
import api from '../api/client';
import { toast } from 'sonner';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const INST_BADGE = {
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Partial: 'bg-blue-50 text-blue-700 border-blue-200',
  Overdue: 'bg-rose-50 text-rose-700 border-rose-200',
};

const LOAN_STATUSES = ['Not Applied', 'Logged In', 'Query Raised', 'Sanctioned', 'Disbursed', 'Rejected'];

const PayDialog = ({ inst, onClose, onSaved }) => {
  const [amt, setAmt] = useState(inst.amount - (inst.paid_amount || 0));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!amt || amt <= 0) { toast.error('Enter amount > 0'); return; }
    setSaving(true);
    try {
      await installmentsApi.pay(inst.id, { paid_amount: Number(amt), notes });
      toast.success('Payment recorded');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-700">Mark payment for <span className="font-semibold">{inst.milestone}</span></div>
      <div><Label>Amount Received (₹)</Label><Input type="number" value={amt} onChange={e => setAmt(e.target.value)} /></div>
      <div><Label>Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="UTR / cheque no. / mode" /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-brand-navy hover:bg-[#13294A]">{saving ? 'Saving...' : 'Confirm'}</Button>
      </div>
    </div>
  );
};

const DocSlot = ({ booking, doc, docType, category, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(doc?.file_url || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (doc) {
        await documentsApi.update(doc.id, { file_url: url, status: url ? 'Received' : 'Pending' });
      } else {
        await documentsApi.add({ booking_id: booking.id, category, doc_type: docType, file_url: url });
      }
      toast.success('Saved');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('booking_id', booking.id);
    fd.append('category', category);
    fd.append('doc_type', docType);
    try {
      await documentsApi.upload(fd);
      toast.success('Uploaded');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!doc) return;
    try { await documentsApi.remove(doc.id); toast.success('Removed'); onChanged(); }
    catch (e) { toast.error('Failed'); }
  };

  const apiBase = (process.env.REACT_APP_BACKEND_URL || '');
  const fileHref = (() => {
    if (!doc?.file_url) return '';
    if (doc.file_url.startsWith('/api')) return `${apiBase}${doc.file_url}`;
    return doc.file_url;
  })();

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`doc-slot-${docType.replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-medium text-slate-800">{docType}</div>
          <div className="text-[11px] text-slate-500">{doc ? doc.status : 'Pending'}</div>
        </div>
        {doc?.file_url && !editing && (
          <a href={fileHref} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline"><Icons.ExternalLink className="w-3.5 h-3.5 mr-1" />Open</Button>
          </a>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Input placeholder="Paste URL (Drive / S3 / public link)" value={url} onChange={e => setUrl(e.target.value)} />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={save} disabled={busy} className="bg-brand-navy">{busy ? '...' : 'Save URL'}</Button>
            <label className="text-xs text-slate-600 inline-flex items-center gap-1 cursor-pointer hover:text-brand-navy">
              <Icons.Upload className="w-3.5 h-3.5" />
              <span>Or upload file</span>
              <input type="file" className="hidden" onChange={e => upload(e.target.files?.[0])} />
            </label>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500 truncate max-w-[240px]">{doc?.file_url || '—'}</div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(true); setUrl(doc?.file_url || ''); }}>
              <Icons.Pencil className="w-3.5 h-3.5" />
            </Button>
            {doc && (
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={remove}>
                <Icons.Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BookingDrawer = ({ bookingId, open, onOpenChange, onChanged }) => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('schedule');
  const [docTypes, setDocTypes] = useState({ finance: [], registry: [] });
  const [payInst, setPayInst] = useState(null);
  const [loanText, setLoanText] = useState('');
  const [loanType, setLoanType] = useState('update');

  const refresh = async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const [b, types] = await Promise.all([bookingsApi.get(bookingId), documentsApi.types()]);
      setBooking(b); setDocTypes(types);
    } catch (e) { toast.error('Failed to load booking'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && bookingId) refresh(); /* eslint-disable-next-line */ }, [open, bookingId]);

  const sendReminder = async (iid) => {
    try { await installmentsApi.remind(iid); toast.success('Reminder sent (mock)'); refresh(); }
    catch (e) { toast.error('Failed'); }
  };

  const downloadDemandLetter = async (iid) => {
    try {
      const resp = await api.get(`/installments/${iid}/demand-letter.pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      refresh();
    } catch (e) {
      toast.error('Failed to generate demand letter');
    }
  };

  const onPaymentSaved = () => { setPayInst(null); refresh(); onChanged && onChanged(); };

  const addLoanActivity = async () => {
    if (!loanText.trim()) { toast.error('Enter loan activity text'); return; }
    try {
      await bookingsApi.addLoanActivity(bookingId, { type: loanType, text: loanText });
      toast.success('Loan activity logged');
      setLoanText('');
      refresh();
    } catch (e) { toast.error('Failed'); }
  };

  const downloadPdf = async () => {
    if (!booking) return;
    try {
      const resp = await api.get(`/bookings/${booking.id}/confirmation.pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  const findDoc = (docType, cat) => booking?.[cat === 'Finance' ? 'finance_docs' : 'registry_docs']?.find(d => d.doc_type === docType);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl w-full overflow-y-auto" data-testid="booking-drawer">
        {loading || !booking ? (
          <div className="space-y-3 mt-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-xl">{booking.customer_name}</SheetTitle>
              <SheetDescription>
                {booking.id} • {booking.project_name} • Unit {booking.unit_no} • {booking.status}
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                size="sm"
                onClick={downloadPdf}
                data-testid="download-booking-pdf"
                className="bg-brand-gold text-brand-navy hover:bg-amber-300"
              >
                <Icons.FileDown className="w-4 h-4 mr-1.5" />Download Confirmation PDF
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="border rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500">Agreed</div>
                <div className="font-semibold text-slate-800">{inr(booking.agreed_value)}</div>
              </div>
              <div className="border rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500">Paid</div>
                <div className="font-semibold text-emerald-700">{inr(booking.total_paid)}</div>
              </div>
              <div className="border rounded-lg p-3">
                <div className="text-[10px] uppercase text-slate-500">Balance</div>
                <div className="font-semibold text-rose-600">{inr(booking.balance)}</div>
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-4">
              <TabsList className="grid grid-cols-4">
                <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
                <TabsTrigger value="finance" data-testid="tab-finance">Finance</TabsTrigger>
                <TabsTrigger value="registry" data-testid="tab-registry">Registry</TabsTrigger>
                <TabsTrigger value="loan" data-testid="tab-loan">Loan Log</TabsTrigger>
              </TabsList>

              <TabsContent value="schedule" className="mt-3 space-y-2">
                {booking.installments.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No installments.</div>}
                {booking.installments.map(i => (
                  <div key={i.id} className="border rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`inst-row-${i.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{i.milestone}</div>
                      <div className="text-[11px] text-slate-500">Due {i.due_date} • {inr(i.amount)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={INST_BADGE[i.is_overdue && i.status === 'Pending' ? 'Overdue' : i.status]}>
                        {i.is_overdue && i.status === 'Pending' ? 'Overdue' : i.status}
                      </Badge>
                      {i.status !== 'Paid' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => downloadDemandLetter(i.id)} data-testid={`demand-${i.id}`} title="Generate Demand Letter PDF">
                            <Icons.FileWarning className="w-3.5 h-3.5 mr-1 text-rose-600" />Letter
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => sendReminder(i.id)} data-testid={`remind-${i.id}`}>
                            <Icons.MessageCircle className="w-3.5 h-3.5 mr-1" />Remind
                          </Button>
                          <Button size="sm" className="bg-brand-navy hover:bg-[#13294A]" onClick={() => setPayInst(i)} data-testid={`pay-${i.id}`}>
                            <Icons.IndianRupee className="w-3.5 h-3.5 mr-1" />Pay
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {payInst && (
                  <div className="border-2 border-brand-gold rounded-lg p-4 bg-amber-50/30">
                    <PayDialog inst={payInst} onClose={() => setPayInst(null)} onSaved={onPaymentSaved} />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="finance" className="mt-3">
                <div className="space-y-3 mb-4 p-3 border rounded-lg bg-slate-50">
                  <div className="text-xs uppercase text-slate-500 tracking-wider">Bank & Loan</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500 text-xs">Bank:</span> <span className="font-medium">{booking.bank_name || '—'}</span></div>
                    <div><span className="text-slate-500 text-xs">Branch:</span> <span className="font-medium">{booking.bank_branch || '—'}</span></div>
                    <div><span className="text-slate-500 text-xs">Employee:</span> <span className="font-medium">{booking.bank_employee_name || '—'}</span></div>
                    <div><span className="text-slate-500 text-xs">Phone:</span> <span className="font-medium">{booking.bank_employee_phone || '—'}</span></div>
                    <div className="col-span-2">
                      <span className="text-slate-500 text-xs">Status:</span> <Badge variant="outline" className="ml-1">{booking.loan_status || '—'}</Badge>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {(docTypes.finance || []).map(dt => (
                    <DocSlot key={dt} booking={booking} doc={findDoc(dt, 'Finance')} docType={dt} category="Finance" onChanged={refresh} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="registry" className="mt-3 space-y-2">
                {(docTypes.registry || []).map(dt => (
                  <DocSlot key={dt} booking={booking} doc={findDoc(dt, 'Registry')} docType={dt} category="Registry" onChanged={refresh} />
                ))}
              </TabsContent>

              <TabsContent value="loan" className="mt-3 space-y-3">
                <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                  <Label className="text-xs">Add Loan Activity</Label>
                  <div className="flex gap-2">
                    <Select value={loanType} onValueChange={setLoanType}>
                      <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['login', 'query', 'update', 'sanction', 'disbursement'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input value={loanText} onChange={e => setLoanText(e.target.value)} placeholder="e.g. Loan logged with HDFC, ref #HD12345" />
                    <Button onClick={addLoanActivity} className="bg-brand-navy">Log</Button>
                  </div>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {(booking.loan_activities || []).length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No loan activity yet.</div>}
                  {(booking.loan_activities || []).map(a => (
                    <div key={a.id} className="border-l-2 border-brand-gold/40 pl-3 py-2">
                      <div className="text-xs text-slate-500">{new Date(a.createdAt).toLocaleString()} • <span className="uppercase font-medium">{a.type}</span> • {a.createdBy}</div>
                      <div className="text-sm text-slate-800">{a.text}</div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default BookingDrawer;
